import React, { useMemo, useState } from 'react';
import type { MessageKindAdapter, AdapterContext } from './types';
import MessageItem from '../components/MessageItem';
import SlotPicker, { SlotState } from '../components/SlotPicker';
import { formatWon } from '../../../utils/number';

/**
 * 패밀리 가입 초대 — 슬롯 협상 + 가격 + accept/reject/counter.
 *
 * counter 는 PATCH /:cid/status (new_status='countered' + context_ref_patch + append_message).
 * accept/reject 는 PATCH /:cid/status (new_status='accepted'|'rejected').
 */

interface InvitationContext {
 invitation_code: string;
 from_mofamily_hash: string;
 to_mofamily_hash: string;
 sharing_type: 'dedicated' | 'shared';
 proposed_slots: number;
 proposed_price: number;
 dedicated_slots: number[];
 shared_slots: number[];
 recipient_peer: string;
}

// ── 메시지 카드 (message_type 별 분기) ─────────────────────────────

const InvitationOfferCard: React.FC<{ payload: any }> = ({ payload }) => (
 <div className="text-sm space-y-1">
  <div><span className="text-gray-500">제안 슬롯:</span> <span className="font-medium">{payload.proposed_slots ?? '-'}개</span></div>
  <div><span className="text-gray-500">슬롯당 일일 가격:</span> <span className="font-medium">{formatWon(payload.proposed_price ?? 0)}</span></div>
  {Array.isArray(payload.dedicated_slots) && payload.dedicated_slots.length > 0 && (
   <div><span className="text-gray-500">독점 슬롯:</span> {payload.dedicated_slots.join(', ')}</div>
  )}
  {Array.isArray(payload.shared_slots) && payload.shared_slots.length > 0 && (
   <div><span className="text-gray-500">공유 슬롯:</span> {payload.shared_slots.join(', ')}</div>
  )}
 </div>
);

// ── 액션 패널 ──────────────────────────────────────────────────────

const ActionPanel: React.FC<{ ctx: AdapterContext }> = ({ ctx }) => {
 const ictx = ctx.conv.context_ref as unknown as InvitationContext;
 const isAcceptable = ctx.conv.status === 'pending' || ctx.conv.status === 'countered';
 const [delegation, setDelegation] = useState<SlotState[]>([]);
 const [slotNumbers, setSlotNumbers] = useState<number[]>([]);
 // 가격은 문자열로 보관 — 빈 칸 허용(0 자동복원 방지). 숫자 비교/전송은 price 로 파생.
 const [priceStr, setPriceStr] = useState<string>(String(ictx.proposed_price));
 const price = parseInt(priceStr, 10) || 0;
 const [counterMessage, setCounterMessage] = useState('');

 // 응답할 차례인가: 마지막 비-system 메시지 발신자가 viewer 가 아니면 viewer 의 차례.
 // role=sender 든 recipient 든 협상 thread 안에선 양측 모두 응답 가능 (역제안의 역제안 등).
 const myParticipant = ctx.participants.find(p =>
  !p.revoked_at && (p.peer_hash === ctx.viewer.peer_hash || p.acting_peer_hash === ctx.viewer.peer_hash),
 );
 const lastNonSysMessage = [...ctx.messages].reverse().find(m => m.message_type !== 'system_notice');
 const itsMyTurn = !lastNonSysMessage || lastNonSysMessage.sender_peer_hash !== ctx.viewer.peer_hash;
 const canAct = !!myParticipant && isAcceptable && itsMyTurn;

 const proposedSlotsCount = useMemo(
  () => delegation.filter(s => s !== 'none').length,
  [delegation],
 );

 // 슬롯/가격 등 '조건' 변동만 수정으로 본다. 메시지는 수락/역제안 어느 쪽에도 첨부될 수 있는
 // 부가 정보이므로 isModified 에서 제외 — 메시지를 입력해도 수락이 비활성화되지 않게 한다.
 // (역제안 활성화는 조건 변동 필요, 수락은 조건 미변동 시 가능 — 메시지와 무관)
 const isModified = useMemo(() => {
  if (price !== ictx.proposed_price) return true;
  if (proposedSlotsCount !== ictx.proposed_slots) return true;
  // 슬롯 분류 변동 비교
  const nextDed = delegation.flatMap((s, i) => (s === 'dedicated' ? [slotNumbers[i]] : [])).sort();
  const nextSha = delegation.flatMap((s, i) => (s === 'shared' ? [slotNumbers[i]] : [])).sort();
  const curDed = [...(ictx.dedicated_slots ?? [])].sort();
  const curSha = [...(ictx.shared_slots ?? [])].sort();
  if (JSON.stringify(nextDed) !== JSON.stringify(curDed)) return true;
  if (JSON.stringify(nextSha) !== JSON.stringify(curSha)) return true;
  return false;
 }, [delegation, slotNumbers, price, proposedSlotsCount, ictx]);

 if (!canAct) return null;

 const onAccept = async () => {
  if (!confirm('이 초대를 수락하시겠습니까?')) return;
  ctx.setBusy(true);
  try {
   const msg = counterMessage.trim();
   // 메시지를 적었으면 수락과 함께 상대에게 전달(대화에 기록). 안 적었으면 상태 전이만.
   await ctx.api.transitionStatus(ctx.conv.id, {
    new_status: 'accepted',
    ...(msg ? { append_message: { message_type: 'invitation_accept', body: msg } } : {}),
   });
   setCounterMessage('');
   await ctx.onChange();
  } catch (e: any) {
   alert(e.message ?? '수락 실패');
  } finally {
   ctx.setBusy(false);
  }
 };

 const onReject = async () => {
  if (!confirm('이 초대를 거절하시겠습니까?')) return;
  ctx.setBusy(true);
  try {
   const msg = counterMessage.trim();
   // 거절 사유를 적었으면 함께 전달(대화에 기록). 안 적었으면 상태 전이만.
   await ctx.api.transitionStatus(ctx.conv.id, {
    new_status: 'rejected',
    ...(msg ? { append_message: { message_type: 'invitation_reject', body: msg } } : {}),
   });
   setCounterMessage('');
   await ctx.onChange();
  } catch (e: any) {
   alert(e.message ?? '거절 실패');
  } finally {
   ctx.setBusy(false);
  }
 };

 const onCounter = async () => {
  const dedicated_slots = delegation.flatMap((s, i) => (s === 'dedicated' ? [slotNumbers[i]] : []));
  const shared_slots = delegation.flatMap((s, i) => (s === 'shared' ? [slotNumbers[i]] : []));
  const proposed_slots = dedicated_slots.length + shared_slots.length;
  if (proposed_slots === 0) { alert('최소 한 슬롯 이상 선택해야 합니다.'); return; }
  // sharing_type 결정: 모두 dedicated 면 dedicated, 아니면 shared 우선
  const sharing_type: 'dedicated' | 'shared' = shared_slots.length > 0 ? 'shared' : 'dedicated';
  const patch = { dedicated_slots, shared_slots, proposed_slots, proposed_price: price, sharing_type };
  ctx.setBusy(true);
  try {
   await ctx.api.transitionStatus(ctx.conv.id, {
    new_status: 'countered',
    context_ref_patch: patch,
    append_message: {
     message_type: 'invitation_counter',
     body: counterMessage || null,
     payload: patch,
    },
   });
   setCounterMessage('');
   await ctx.onChange();
  } catch (e: any) {
   alert(e.message ?? '역제안 실패');
  } finally {
   ctx.setBusy(false);
  }
 };

 return (
  <div className="mb-4 border border-gray-200 p-4 bg-gray-50">
   <div className="text-sm font-semibold mb-3">협상</div>

   <div className="mb-3">
    <label className="block text-xs text-gray-600 mb-1">슬롯 위임 (클릭하여 독점 → 공유 → 해제)</label>
    <SlotPicker
     toFamilyHash={ictx.to_mofamily_hash}
     selectedFamilyHash={ictx.to_mofamily_hash}
     initial={{ dedicated: ictx.dedicated_slots ?? [], shared: ictx.shared_slots ?? [] }}
     onChange={(d, n) => { setDelegation(d); setSlotNumbers(n); }}
     disabled={ctx.busy}
    />
    <div className="text-xs text-gray-500 mt-1">선택된 슬롯: {proposedSlotsCount}개</div>
   </div>

   <div className="mb-3">
    <label className="block text-xs text-gray-600 mb-1">슬롯당 일일 가격 (원)</label>
    <input
     type="number"
     min={0}
     step={1000}
     value={priceStr}
     onChange={e => setPriceStr(e.target.value)}
     disabled={ctx.busy}
     className="w-full px-2 py-1 border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
    />
   </div>

   {/* 메시지 — 수락/역제안 어느 쪽이든 입력하면 상대에게 함께 전달(대화에 기록). 선택 사항. */}
   <div className="mb-3">
    <label className="block text-xs text-gray-600 mb-1">메시지 (선택)</label>
    <textarea
     rows={2}
     value={counterMessage}
     onChange={e => setCounterMessage(e.target.value)}
     disabled={ctx.busy}
     placeholder="상대에게 전할 메시지를 적어주세요"
     className="w-full px-2 py-1 border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
    />
   </div>

   <div className="flex gap-2">
    <button
     onClick={onAccept}
     disabled={ctx.busy || isModified}
     title={isModified ? '슬롯/가격을 수정하면 수락할 수 없습니다. 그 값으로 역제안하거나 원래대로 되돌린 뒤 수락하세요.' : ''}
     className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white text-sm disabled:opacity-50"
    >수락</button>
    <button
     onClick={onCounter}
     disabled={ctx.busy || !isModified || proposedSlotsCount === 0}
     title={!isModified ? '슬롯/가격을 변경해야 역제안할 수 있습니다.' : ''}
     className="px-3 py-1 bg-orange-500 hover:bg-orange-600 text-white text-sm disabled:opacity-50"
    >역제안</button>
    <button
     onClick={onReject}
     disabled={ctx.busy}
     className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm disabled:opacity-50"
    >거절</button>
   </div>
  </div>
 );
};

// ── 어댑터 ─────────────────────────────────────────────────────────

const invitationAdapter: MessageKindAdapter = {
 kind: 'invitation',
 renderMessage: (m, ctx) => (
  <MessageItem key={m.id} message={m} viewerPeerHash={ctx.viewer.peer_hash}>
   {m.body && <div className="text-sm whitespace-pre-wrap mb-1">{m.body}</div>}
   {(m.message_type === 'invitation_offer' || m.message_type === 'invitation_counter') && (
    <InvitationOfferCard payload={m.payload} />
   )}
  </MessageItem>
 ),
 renderActionPanel: ctx => <ActionPanel ctx={ctx} />,
};

export default invitationAdapter;
